import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Award, Clock, Users, Star, CheckCircle, Loader } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { usePrepAuth } from '../../contexts/PrepAuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export const PrepPrograms = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolledIds, setEnrolledIds] = useState(new Set());
  const [enrollingId, setEnrollingId] = useState(null);
  const [pageContent, setPageContent] = useState({ hero_headline: 'Allied Health Certification Prep', hero_description: 'Structured certification prep designed to help you pass your exam and start your career.' });
  const { isAuthenticated } = usePrepAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPrograms();
    axios.get(`${API}/prep/content/prep-programs`).then(r => setPageContent(prev => ({ ...prev, ...r.data }))).catch(console.error);
    if (isAuthenticated) fetchEnrollments();
  }, [isAuthenticated]);

  const fetchPrograms = async () => {
    try {
      const r = await axios.get(`${API}/prep/programs`);
      setPrograms(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrollments = async () => {
    try {
      const r = await axios.get(`${API}/prep/enrollments`, { withCredentials: true });
      setEnrolledIds(new Set(r.data.filter(e => e.status === 'active').map(e => e.program_id)));
    } catch (e) {
      console.error(e);
    }
  };

  const createOrder = async (programId) => {
    const r = await axios.post(`${API}/prep/payments/create-order`, {
      program_id: programId,
      origin_url: window.location.origin
    }, { withCredentials: true });
    return r.data.order_id;
  };

  const onApprove = async (data, programId) => {
    try {
      await axios.post(`${API}/prep/payments/capture-order`, {
        order_id: data.orderID,
        program_id: programId,
        origin_url: window.location.origin
      }, { withCredentials: true });
      toast.success('Enrollment successful!');
      navigate('/prep/payment/success');
    } catch {
      toast.error('Payment capture failed. Please contact support.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading programs...</div>
      </div>
    );
  }

  return (
    <PayPalScriptProvider options={{ "client-id": PAYPAL_CLIENT_ID, currency: "USD" }}>
    <div className="min-h-screen">
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {pageContent.hero_headline.includes('Certification')
              ? <>{pageContent.hero_headline.split('Certification')[0]}<span className="text-blue-300">Certification</span>{pageContent.hero_headline.split('Certification')[1]}</>
              : pageContent.hero_headline}
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl">{pageContent.hero_description}</p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {programs.map((program) => (
              <Card key={program.id} className="border-2 hover:shadow-2xl transition-all">
                <CardHeader>
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Award className="text-blue-600" size={36} />
                        <div>
                          <CardTitle className="text-3xl">{program.title}</CardTitle>
                          <div className="flex items-center gap-4 mt-2">
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Active</Badge>
                            <div className="flex items-center gap-1 text-yellow-500">
                              <Star size={16} fill="currentColor" />
                              <span className="text-sm font-semibold text-gray-700">
                                {program.rating} ({program.enrollment_count} students)
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <CardDescription className="text-lg mt-4">{program.description}</CardDescription>
                    </div>
                    <div className="flex flex-col items-start lg:items-end gap-3">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Clock size={20} />
                        <span className="font-medium">{program.duration}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users size={20} />
                        <span className="font-medium">{program.enrollment_count}+ enrolled</span>
                      </div>
                      <div className="text-4xl font-bold text-blue-600">${program.price}</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2">
                      <h4 className="font-semibold text-gray-900 mb-4 text-lg">What's Included:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {program.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                            <span className="text-gray-600">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="lg:col-span-1">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border-2 border-blue-200">
                        <h4 className="font-semibold text-gray-900 mb-4 text-lg">Get Started Today</h4>
                        {enrolledIds.has(program.id) ? (
                          <div className="space-y-3">
                            <div className="bg-green-100 text-green-700 text-center py-3 rounded-lg font-semibold">
                              Already Enrolled
                            </div>
                            <Link to="/prep/dashboard">
                              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                                Go to Dashboard
                              </Button>
                            </Link>
                          </div>
                        ) : isAuthenticated ? (
                          <div className="space-y-3">
                            <p className="text-sm text-gray-600 mb-2 font-medium">Enroll Now — ${program.price}:</p>
                            <PayPalButtons
                              style={{ layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay' }}
                              createOrder={() => createOrder(program.id)}
                              onApprove={(data) => onApprove(data, program.id)}
                              onError={() => toast.error('PayPal error. Please try again.')}
                            />
                            <p className="text-xs text-center text-gray-500">Secure checkout powered by PayPal</p>
                            <Button variant="outline" className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
                              Schedule Consultation
                            </Button>
                            <div className="pt-4 border-t border-blue-300">
                              <p className="text-sm text-gray-700 text-center">Need individual materials?</p>
                              <Link to="/prep/individual">
                                <Button variant="link" className="w-full text-blue-600 hover:text-blue-700">
                                  Browse Individual Options
                                </Button>
                              </Link>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <Link to="/prep/login">
                              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6" data-testid="enroll-login-btn">
                                Login to Enroll — ${program.price}
                              </Button>
                            </Link>
                            <Button variant="outline" className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
                              Schedule Consultation
                            </Button>
                            <div className="pt-4 border-t border-blue-300">
                              <p className="text-sm text-gray-700 text-center">Need individual materials?</p>
                              <Link to="/prep/individual">
                                <Button variant="link" className="w-full text-blue-600 hover:text-blue-700">
                                  Browse Individual Options
                                </Button>
                              </Link>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Flexible Learning Options</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-2 hover:shadow-lg transition-all">
              <CardHeader>
                <CardTitle className="text-xl">Full Program Access</CardTitle>
                <CardDescription>Complete certification preparation with all materials and support</CardDescription>
              </CardHeader>
              <CardContent>
                <a href="#programs-section">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" onClick={() => window.scrollTo({top: 0, behavior:'smooth'})}>Enroll in Program</Button>
                </a>
              </CardContent>
            </Card>
            <Card className="border-2 hover:shadow-lg transition-all">
              <CardHeader>
                <CardTitle className="text-xl">Individual Materials</CardTitle>
                <CardDescription>Purchase specific study guides, videos, or practice tests</CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/prep/individual">
                  <Button variant="outline" className="w-full border-blue-600 text-blue-600 hover:bg-blue-50">Browse Materials</Button>
                </Link>
              </CardContent>
            </Card>
            <Card className="border-2 hover:shadow-lg transition-all">
              <CardHeader>
                <CardTitle className="text-xl">One-on-One Sessions</CardTitle>
                <CardDescription>Schedule personalized tutoring with experienced instructors</CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/contact">
                  <Button variant="outline" className="w-full border-blue-600 text-blue-600 hover:bg-blue-50">Book Session</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
    </PayPalScriptProvider>
  );
};
